import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { FormlyFieldConfig } from '@ngx-formly/core';

import { ReviewRow, ReviewSection } from './wizard.types';
import { CONTAINER_TYPES, formatDisplayValue } from './review-value.util';
import { isDisplayOnlyFieldType } from './display-only-field-types';

/**
 * Field types beyond the form builder's own "Opmaak formulier" catalogue
 * (`isDisplayOnlyFieldType`) that also carry no input value and must not print a summary row.
 * Neither is an `INPUT_OPTIONS` member (verified by grep — the form builder never registers a
 * `button` or `template` field type), so they cannot be derived and are named here instead of
 * silently folded back into a hand list.
 */
const WIZARD_REVIEW_EXTRA_DISPLAY_ONLY = new Set(['button', 'template']);

/** True for a field type that renders content but never collects a summarisable value. */
function isReviewDisplayOnly(type: string | undefined): boolean {
  return !!type && (isDisplayOnlyFieldType(type) || WIZARD_REVIEW_EXTRA_DISPLAY_ONLY.has(type));
}

// Formatting rules and the masking/container/technical-subkey sets live in review-value.util.ts,
// shared with the admin's onboarding-review card (readonly-onboarding) so the two surfaces
// cannot drift.

/**
 * Samenvattings-stap voor de wizard (Figma "Review answers", node 2325-104639): per stap een
 * sectie met de staptitel, een Bewerken-knop terug naar die stap en per veld een regel met
 * label + ingevulde waarde. Lege velden tonen een badge: "Ontbreekt" (verplicht) of
 * "Optioneel". Optioneel sluit een bevestigings-checkbox de samenvatting af; de wizard gate't
 * er zijn afrondknoppen mee. Puur presentational — de wizard levert de zichtbare stappen aan
 * en luistert naar `editStep`/`confirmedChange`.
 */
@Component({
  selector: 'app-wizard-review',
  imports: [MatButtonModule, MatCheckboxModule, MatIconModule],
  template: `
    @if (heading(); as title) {
      <h2 class="mb-6 text-xl font-medium leading-tight">{{ title }}</h2>
    }

    @for (section of sections(); track section.stepIndex) {
      <div class="w-full" [class.mt-6]="!$first">
        <div class="flex items-center gap-5">
          <div class="flex-1 text-[15px] font-medium leading-tight">{{ section.label }}</div>
          <button
            mat-stroked-button
            type="button"
            class="!h-8 !min-h-0 !rounded-lg !px-3 !text-sm"
            (click)="editStep.emit(section.stepIndex)">
            Bewerken
          </button>
        </div>

        <div class="mt-1">
          <!-- Track by index: rows are a flattened view of nested groups, so two fields in one
               step can legitimately carry the same label (two address blocks both showing
               "Postcode") and a label key would collide. -->
          @for (row of section.rows; track $index) {
            <div class="flex items-start gap-2 border-b py-3 last:border-b-0">
              <div class="w-2/5 shrink-0 text-sm font-medium text-accent">{{ row.label }}</div>
              @if (row.value !== null) {
                <div class="min-w-0 flex-1 break-words text-sm font-medium">{{ row.value }}</div>
              } @else if (row.required) {
                <span
                  class="inline-flex items-center gap-1 rounded-lg bg-[#fff8e0] px-2 py-1 text-xs font-medium text-[#8a6000] dark:bg-amber-400/10 dark:text-amber-400">
                  <mat-icon class="icon-size-3 text-current" svgIcon="heroicons_outline:exclamation-circle"></mat-icon>
                  Ontbreekt
                </span>
              } @else {
                <span
                  class="inline-flex items-center rounded-lg bg-[#eeeff1] px-2 py-1 text-xs font-medium text-black dark:bg-[#3a3a3c] dark:text-gray-200">
                  Optioneel
                </span>
              }
            </div>
          }
        </div>
      </div>
    }

    @if (confirmText(); as text) {
      <div class="mt-6">
        <mat-checkbox [checked]="confirmed()" (change)="onConfirmedChange($event.checked)">
          <span class="inline-block text-sm leading-normal text-accent">{{ text }}</span>
        </mat-checkbox>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WizardReviewComponent {
  /** Zichtbare wizard-stappen (formly step-configs) waarvan de waardes samengevat worden. */
  readonly steps = input.required<FormlyFieldConfig[]>();
  /** Bump om de samenvatting te herberekenen bij form-wijzigingen (formly muteert in place). */
  readonly revision = input<number>(0);
  /** Kop boven de samenvatting (Figma "Review answers"); `null` wanneer de host de kop al toont. */
  readonly heading = input<string | null>('Controleren');
  /** Tekst bij de bevestigings-checkbox onderaan; `null`/leeg = geen checkbox. */
  readonly confirmText = input<string | null>(null);
  /** Checkbox-status — de wizard is eigenaar zodat een form-reset ('Extra toevoegen') hem wist. */
  readonly confirmed = input<boolean>(false);
  /** Index (binnen de zichtbare stappen) van de stap die de gebruiker wil bewerken. */
  readonly editStep = output<number>();
  /** Status van de bevestigings-checkbox — de wizard gate't er zijn afrondknoppen mee. */
  readonly confirmedChange = output<boolean>();

  protected onConfirmedChange(checked: boolean): void {
    this.confirmedChange.emit(checked);
  }

  protected readonly sections = computed<ReviewSection[]>(() => {
    this.revision();
    return this.steps().map((step, stepIndex) => ({
      stepIndex,
      label: (step.props?.label as string) ?? `Stap ${stepIndex + 1}`,
      rows: this.#collectRows(step.fieldGroup ?? []),
    }));
  });

  #collectRows(fields: FormlyFieldConfig[]): ReviewRow[] {
    const rows: ReviewRow[] = [];
    for (const field of fields) {
      if (field.hide === true || field.className?.includes('hidden')) {
        continue;
      }
      const type = typeof field.type === 'string' ? field.type : undefined;
      if (isReviewDisplayOnly(type)) {
        continue;
      }
      const label = field.props?.label as string | undefined;
      if (field.fieldGroup?.length && (!label || CONTAINER_TYPES.has(type))) {
        rows.push(...this.#collectRows(field.fieldGroup));
        continue;
      }
      if (!label) {
        continue;
      }
      // Live fields carry runtime-refilled props.options — pass them so the applicant confirms
      // option LABELS, exactly what the reviewer's card shows for the same answer. The
      // persisted optionsArray rides along as the same fallback the review card has: without
      // it an un-refilled options list would show the applicant the raw token while the
      // reviewer reads the label. Masking of secret types happens inside the shared formatter.
      const formatted = formatDisplayValue(field.formControl?.value, {
        type,
        options: field.props?.options,
        optionsArray: field.props?.['optionsArray'],
        addonLeft: (field.props?.['addonLeft'] as { text?: string } | undefined)?.text,
        addonRight: (field.props?.['addonRight'] as { text?: string } | undefined)?.text,
      });
      rows.push({
        label,
        required: field.props?.required === true,
        value: formatted,
      });
    }
    return rows;
  }
}
